import React, { FC, useEffect, useState } from "react";
import { Box, Typography, CircularProgress, IconButton, Skeleton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

interface SystemStatusData {
	dhcpcdStatus: string;
	networkInfo: string;
}

const SystemStatus: FC = () => {
	const [systemStatus, setSystemStatus] = useState<SystemStatusData | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [refreshing, setRefreshing] = useState<boolean>(false);

	useEffect(() => {
		loadSystemStatus();
		// Auto-refresh system status every 30 seconds
		const interval = setInterval(loadSystemStatus, 30000);
		return () => clearInterval(interval);
	}, []);

	const loadSystemStatus = async () => {
		try {
			setRefreshing(true);
			const response = await fetch(`http://${window.location.hostname}:81/api/system-status`);
			const data = await response.json();

			if (response.ok) {
				setSystemStatus(data);
			} else {
				throw new Error(data.error || "Failed to load system status");
			}
		} catch (error) {
			console.error("Error loading system status:", error);
		} finally {
			setLoading(false);
			setRefreshing(false);
		}
	};

	const formatNetworkInfo = (networkInfo: string) => {
		if (!networkInfo) return [];

		// Split the network info into lines and filter out empty lines
		const lines = networkInfo.split("\n").filter((line) => line.trim());

		const formattedLines: Array<{ type: "interface" | "detail" | "status"; content: string; interfaceName?: string }> =
			[];

		lines.forEach((line) => {
			const trimmedLine = line.trim();

			// Check if this is an interface line (contains interface number and name)
			if (/^\d+:\s+\w+/.test(trimmedLine)) {
				formattedLines.push({
					type: "interface",
					content: trimmedLine,
					interfaceName: trimmedLine.split(":")[1]?.trim().split(" ")[0]
				});
			}
			// Check if this is a status line (contains state UP/DOWN/UNKNOWN)
			else if (
				trimmedLine.includes("state") &&
				(trimmedLine.includes("UP") || trimmedLine.includes("DOWN") || trimmedLine.includes("UNKNOWN"))
			) {
				formattedLines.push({
					type: "status",
					content: trimmedLine
				});
			}
			// All other lines are details
			else {
				formattedLines.push({
					type: "detail",
					content: trimmedLine
				});
			}
		});

		return formattedLines;
	};

	const getStatusColor = (status: string) => {
		if (status.includes("UP")) return "#2e7d32";
		if (status.includes("DOWN")) return "#d32f2f";
		return "#6c757d";
	};

	const getStatusIcon = (status: string) => {
		if (status.includes("UP")) return <CheckCircleIcon sx={{ fontSize: "16px", color: "#2e7d32" }} />;
		if (status.includes("DOWN")) return <ErrorIcon sx={{ fontSize: "16px", color: "#d32f2f" }} />;
		return null;
	};

	return (
		<Box
			className="network-setting-section-wrapper"
			sx={{
				background: "#ffffff",
				borderRadius: "12px",
				boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
				border: "1px solid rgba(0, 0, 0, 0.06)",
				alignItems: "stretch"
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
				<Box>
					<Typography variant="h6" color="var(--color-primary-main)">
						System Status
					</Typography>
					<Typography variant="body1" color="#616161">
						Network system status and service information
					</Typography>
				</Box>

				<IconButton
					onClick={loadSystemStatus}
					disabled={refreshing}
					sx={{
						color: "var(--color-primary-main)",
						"&:hover": {
							backgroundColor: "rgba(103, 58, 183, 0.04)"
						}
					}}
				>
					{refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
				</IconButton>
			</Box>

			{loading ? (
				<Box>
					{/* Header skeleton */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
						<Skeleton variant="circular" width={20} height={20} />
						<Skeleton variant="text" width={200} height={24} />
					</Box>

					{/* Content skeleton */}
					<Box
						sx={{
							background: "#f8f9fa",
							borderRadius: "12px",
							padding: "24px",
							border: "1px solid #e9ecef",
							width: "100%"
						}}
					>
						{/* Network interfaces section skeleton */}
						<Box>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
								<Skeleton variant="circular" width={20} height={20} />
								<Skeleton variant="text" width={180} height={20} />
							</Box>

							<Box
								sx={{
									background: "#ffffff",
									borderRadius: "8px",
									border: "1px solid #e9ecef",
									overflow: "hidden"
								}}
							>
								{/* Header skeleton */}
								<Box
									sx={{
										background: "#f8f9fa",
										padding: "12px 16px",
										borderBottom: "1px solid #e9ecef"
									}}
								>
									<Skeleton variant="text" width={150} height={16} />
								</Box>

								{/* Content skeleton */}
								<Box sx={{ padding: "16px" }}>
									{/* Interface lines skeleton */}
									{[1, 2, 3, 4, 5].map((index) => (
										<Box key={index} sx={{ mb: 2 }}>
											<Skeleton variant="text" width="100%" height={16} />
											<Skeleton variant="text" width="80%" height={14} />
											<Skeleton variant="text" width="60%" height={14} />
										</Box>
									))}
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			) : systemStatus ? (
				<Box
					sx={{
						background: "#f8f9fa",
						borderRadius: "12px",
						padding: "24px",
						border: "1px solid #e9ecef"
					}}
				>
					{/* Network Interfaces */}
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
							<NetworkCheckIcon sx={{ color: "var(--color-primary-main)", fontSize: "20px" }} />
							<Typography
								variant="subtitle1"
								color="var(--color-primary-main)"
								fontWeight={600}
								sx={{ fontSize: "16px" }}
							>
								Network Interfaces
							</Typography>
						</Box>

						{systemStatus.networkInfo ? (
							<Box
								sx={{
									background: "#ffffff",
									borderRadius: "8px",
									border: "1px solid #e9ecef",
									overflow: "hidden"
								}}
							>
								<Box
									sx={{
										background: "#f8f9fa",
										padding: "12px 16px",
										borderBottom: "1px solid #e9ecef"
									}}
								>
									<Typography
										variant="caption"
										color="#6c757d"
										fontWeight={600}
										sx={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}
									>
										Interface Configuration
									</Typography>
								</Box>

								<Box
									sx={{
										padding: "16px",
										maxHeight: "400px",
										overflowY: "auto",
										fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace",
										fontSize: "13px",
										lineHeight: 1.6
									}}
								>
									{formatNetworkInfo(systemStatus.networkInfo).map((line, index) => (
										<Box
											key={index}
											sx={{
												display: "flex",
												alignItems: "flex-start",
												gap: 1,
												mb: line.type === "interface" ? 1 : 0.5,
												mt: index > 0 && line.type === "interface" ? 2 : 0.5,
												pb: line.type === "interface" ? 1 : 0,
												pt: index > 0 && line.type === "interface" ? 1 : 0,
												borderBottom: line.type === "interface" ? "1px solid #f1f3f4" : "none",
												borderTop: index > 0 && line.type === "interface" ? "1px solid #f1f3f4" : "none"
											}}
										>
											{line.type === "status" && (
												<Box sx={{ mt: 0.5, flexShrink: 0 }}>{getStatusIcon(line.content)}</Box>
											)}
											<Typography
												variant="body2"
												sx={{
													fontFamily: "inherit",
													fontSize: "inherit",
													lineHeight: "inherit",
													color:
														line.type === "interface"
															? "#212529"
															: line.type === "status"
															? getStatusColor(line.content)
															: "#6c757d",
													fontWeight: line.type === "interface" ? 600 : 400,
													flex: 1,
													wordBreak: "break-all"
												}}
											>
												{line.content}
											</Typography>
										</Box>
									))}
								</Box>
							</Box>
						) : (
							<Box
								sx={{
									textAlign: "center",
									py: 4,
									color: "#6c757d",
									background: "#ffffff",
									borderRadius: "8px",
									border: "1px dashed #dee2e6"
								}}
							>
								<NetworkCheckIcon sx={{ fontSize: 48, color: "#dee2e6", mb: 2 }} />
								<Typography variant="body2" sx={{ fontSize: "14px" }}>
									No network interface information available
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
			) : (
				<Box
					sx={{
						textAlign: "center",
						py: 6,
						color: "#6c757d",
						background: "#f8f9fa",
						borderRadius: "12px",
						border: "1px solid #e9ecef"
					}}
				>
					<ErrorIcon sx={{ fontSize: 48, color: "#dee2e6", mb: 2 }} />
					<Typography variant="body2" sx={{ fontSize: "14px" }}>
						Failed to load system status
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default SystemStatus;
